import { type FC, type ReactElement } from 'react'
import { Spinner } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../../../../components/Button/Button'
import { type IntegrationWorkflow, type IntegrationWorkflowCapabilities, type IntegrationWorkflowStep } from '../../../../../../models/IntegrationWorkflow'
import { CompleteStepForm } from './CompleteStepForm'
import {
  type CompleteIntegrationWorkflowStepInput,
  getWorkflowStepPosition,
  type IntegrationWorkflowSuggestions,
  type IntegrationWorkflowTarget,
} from './integrationWorkflow'
import { StartWorkflowForm } from './StartWorkflowForm'
import { useCompleteIntegrationWorkflowStep } from './useIntegrationWorkflowActions'

interface StepSuggestionProps {
  target: IntegrationWorkflowTarget
  workflow: IntegrationWorkflow
  step: IntegrationWorkflowStep
  capabilities: IntegrationWorkflowCapabilities
  onCompleted: (workflowId: number) => void
}

const StepSuggestion: FC<StepSuggestionProps> = ({ target, workflow, step, capabilities, onCompleted }) => {
  const [complete, { isLoading, isError }] = useCompleteIntegrationWorkflowStep(target)
  const position = getWorkflowStepPosition(workflow)

  const completeStep = async (input: CompleteIntegrationWorkflowStepInput) => {
    const result = await complete(workflow.id, step.id, input)
    if (result.data) {
      onCompleted(workflow.id)
    }
  }

  return (
    <div className='d-flex flex-column px-4 py-3'>
      <div className='d-flex align-items-center justify-content-between gap-3'>
        <span className='paragraph-medium bold'>{workflow.name}</span>
        {position ? (
          <span className='paragraph-extra-small zocks-text-gray-600 text-nowrap'>
            Step {position.number} of {position.total}
          </span>
        ) : null}
      </div>
      <span className='paragraph-small'>{step.name}</span>
      <CompleteStepForm step={step} showNote={capabilities.stepNote} isSubmitting={isLoading} submitLabel='Mark step as completed' onComplete={completeStep} />
      {isError ? <span className='paragraph-extra-small zocks-text-danger pt-2 text-end'>We could not complete this step. Please try again.</span> : null}
    </div>
  )
}

const SectionHeading: FC<{ title: string }> = ({ title }) => <div className='zocks-bg-color-gray-100 paragraph-small bold px-4 py-2'>{title}</div>

interface SuggestionsTabProps {
  target: IntegrationWorkflowTarget
  suggestions: IntegrationWorkflowSuggestions & {
    isLoading: boolean
    rerun: () => void
    markWorkflowHandled: (workflowId: number) => void
    markTemplateHandled: (templateId: string) => void
  }
  capabilities: IntegrationWorkflowCapabilities
  onStarted: (templateId: string) => void
}

export const SuggestionsTab: FC<SuggestionsTabProps> = ({ target, suggestions, capabilities, onStarted }) => {
  const stepSuggestions = suggestions.workflows.flatMap((workflow) =>
    workflow.activeStep
      ? [
          <StepSuggestion
            key={`workflow-${workflow.id}`}
            target={target}
            workflow={workflow}
            step={workflow.activeStep}
            capabilities={capabilities}
            onCompleted={suggestions.markWorkflowHandled}
          />,
        ]
      : [],
  )
  const templateSuggestions = suggestions.templates.map(({ template, label, summary }) => (
    <div key={`template-${template.id}`} className='d-flex flex-column gap-3 px-4 py-3'>
      <span className='paragraph-medium bold'>{template.name}</span>
      <StartWorkflowForm target={target} template={template} suggestedLabel={label} suggestedSummary={summary} onStarted={() => onStarted(template.id)} />
    </div>
  ))
  const blocks: ReactElement[] = [
    ...(stepSuggestions.length ? [<SectionHeading key='running-heading' title='Running workflow' />, ...stepSuggestions] : []),
    ...(templateSuggestions.length ? [<SectionHeading key='new-heading' title='New workflow' />, ...templateSuggestions] : []),
  ]

  const body = suggestions.isLoading ? (
    <div role='status' className='d-flex align-items-center justify-content-center gap-2 p-4 paragraph-small zocks-text-gray-600'>
      <Spinner size='sm' />
      Loading workflow suggestions
    </div>
  ) : blocks.length ? (
    <div className='border zocks-border-color-gray-300 rounded-2'>
      {blocks.map((block, index) => (
        <div key={block.key} className={classNames({ 'border-top zocks-border-color-gray-300': index > 0, 'rounded-top-2 overflow-hidden': index === 0 })}>
          {block}
        </div>
      ))}
    </div>
  ) : (
    <div className='p-4 text-center paragraph-small zocks-text-gray-600'>There are no workflow suggestions for this meeting.</div>
  )

  return (
    <div className='d-flex flex-column gap-3 mt-3'>
      <div className='d-flex align-items-center justify-content-between gap-3'>
        <span className='paragraph-small'>Suggestions based on the meeting details</span>
        <Button variant='primaryLinkButton' className='button-small' disabled={suggestions.isLoading} onClick={suggestions.rerun}>
          <i className='bi bi-arrow-repeat' />
          Rerun
        </Button>
      </div>
      {body}
    </div>
  )
}
